import { queryOptions } from "@tanstack/react-query";

import { getOperatorsHttpOrigin } from "../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";
import { OperatorsForbiddenError, operatorsQueryKeys } from "../../lib/operatorsReactQuery";
import {
  BOOLEAN_FILTERS,
  OUTREACH_GROUP_FILTERS,
  isReengagementReportResponse,
  isReengagementWaveEmailsResponse,
  isValidMonthKey,
  type ReengagementEmailFilters,
  type ReengagementReportResponse,
  type ReengagementWaveEmailsResponse,
} from "./reengagementEmailsTypes";

const REQUEST_TIMEOUT_MS = 15_000;

export const reengagementEmailsQueryKeys = {
  all: [...operatorsQueryKeys.all, "dashboards", "reengagement-emails"] as const,
  report: (month?: string) =>
    [...reengagementEmailsQueryKeys.all, "report", month ?? null] as const,
  waveEmails: (
    month: string,
    waveDate: string,
    emailPage: number,
    filters: ReengagementEmailFilters,
  ) =>
    [
      ...reengagementEmailsQueryKeys.all,
      "wave-emails",
      month,
      waveDate,
      emailPage,
      filters,
    ] as const,
};

function throwIfUnauthorized(response: Response): void {
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
}

async function throwIfForbidden(response: Response, fallbackMessage: string): Promise<void> {
  if (response.status !== 403) {
    return;
  }
  const message = await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage);
  throw new OperatorsForbiddenError(message || fallbackMessage);
}

async function fetchJson(
  path: string,
  validate: (data: unknown) => boolean,
  options: { signal?: AbortSignal } = {},
): Promise<unknown> {
  const { signal } = options;
  const controller = new AbortController();
  const timeoutError =
    typeof DOMException === "function"
      ? new DOMException("Request timed out", "AbortError")
      : Object.assign(new Error("Request timed out"), { name: "AbortError" });
  const timeoutId = setTimeout(() => controller.abort(timeoutError), REQUEST_TIMEOUT_MS);
  let removeAbortListener = () => {};

  if (signal) {
    if (signal.aborted) {
      controller.abort(signal.reason);
    } else {
      const onAbort = () => controller.abort(signal.reason);
      signal.addEventListener("abort", onAbort, { once: true });
      removeAbortListener = () => signal.removeEventListener("abort", onAbort);
    }
  }

  try {
    const response = await fetch(`${getOperatorsHttpOrigin()}${path}`, {
      credentials: "include",
      headers: { "content-type": "application/json" },
      signal: controller.signal,
    });

    throwIfUnauthorized(response);
    await throwIfForbidden(response, "You do not have access to Dashboards.");

    if (!response.ok) {
      const message = await readOperatorsAuthErrorMessage(response).catch(async () => {
        const body = await response.text();
        return `${response.status} ${body}`.trim();
      });
      throw new Error(message || "Failed to load re-engagement email data");
    }

    const data: unknown = await response.json();
    if (!validate(data)) {
      throw new Error("Invalid response: missing re-engagement dashboard fields");
    }
    return data;
  } catch (error) {
    if (controller.signal.aborted && !signal?.aborted) {
      throw new Error("Request timed out", { cause: error });
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
    removeAbortListener();
  }
}

export async function fetchReengagementEmailReport(
  month?: string,
  options: { signal?: AbortSignal } = {},
): Promise<ReengagementReportResponse> {
  if (month !== undefined && month !== null && month !== "") {
    if (!isValidMonthKey(month)) {
      throw new Error("Invalid month parameter: expected YYYY-MM format");
    }
  }

  const params = new URLSearchParams();
  if (month) params.set("month", month);
  const query = params.size ? `?${params.toString()}` : "";
  const data = await fetchJson(
    `/api/v1/dashboards/reengagement-emails${query}`,
    isReengagementReportResponse,
    options,
  );
  return data as ReengagementReportResponse;
}

export function reengagementEmailReportQueryOptions(month?: string) {
  return queryOptions({
    queryKey: reengagementEmailsQueryKeys.report(month),
    queryFn: ({ signal }) => fetchReengagementEmailReport(month, { signal }),
  });
}

export async function fetchReengagementWaveEmails(
  month: string,
  waveDate: string,
  emailPage = 1,
  filters: Partial<ReengagementEmailFilters> = {},
  options: { signal?: AbortSignal } = {},
): Promise<ReengagementWaveEmailsResponse> {
  if (!isValidMonthKey(month)) {
    throw new Error("Invalid month parameter: expected YYYY-MM format");
  }
  if (typeof waveDate !== "string" || !waveDate.startsWith(`${month}-`)) {
    throw new Error("Invalid wave date: expected a date within the month");
  }
  if (!Number.isInteger(emailPage) || emailPage < 1) {
    throw new Error("Invalid email page: expected a positive integer");
  }

  const normalizedFilters: ReengagementEmailFilters = {
    outreachGroup: filters.outreachGroup || "all",
    replied: filters.replied || "all",
    booked: filters.booked || "all",
    encountered: filters.encountered || "all",
  };
  if (
    !OUTREACH_GROUP_FILTERS.has(normalizedFilters.outreachGroup) ||
    !BOOLEAN_FILTERS.has(normalizedFilters.replied) ||
    !BOOLEAN_FILTERS.has(normalizedFilters.booked) ||
    !BOOLEAN_FILTERS.has(normalizedFilters.encountered)
  ) {
    throw new Error("Invalid email table filter");
  }

  const params = new URLSearchParams({ month, waveDate });
  if (emailPage > 1) params.set("emailPage", String(emailPage));
  for (const [key, value] of Object.entries(normalizedFilters)) {
    if (value !== "all") params.set(key, value);
  }

  const data = await fetchJson(
    `/api/v1/dashboards/reengagement-emails?${params.toString()}`,
    isReengagementWaveEmailsResponse,
    options,
  );
  return data as ReengagementWaveEmailsResponse;
}
